'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, Sparkles } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

interface AnonymousLimitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  availableCharacters: number;
  attemptedCharacters?: number;
  insufficientBy?: number;
}

export function AnonymousLimitDialog({
  isOpen,
  onClose,
  availableCharacters,
  attemptedCharacters = 0,
  insufficientBy = 0
}: AnonymousLimitDialogProps) {
  const router = useRouter();
  const t = useTranslations('limitDialogs.anonymous');

  const handleCreateAccount = () => {
    onClose();
    router.push('/sign-up');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
              <Zap className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <DialogTitle className="text-start">{t('title')}</DialogTitle>
              <DialogDescription className="text-start">
                {t('description')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto flex-1 min-h-0 -mx-6 px-6">
          {/* Current Usage */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-medium mb-2">{t('creditSnapshot')}</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('availableCredits')}</span>
                <Badge variant="secondary">{availableCharacters.toLocaleString()}</Badge>
              </div>
              {attemptedCharacters > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('attemptedTranslation')}</span>
                  <Badge variant="destructive" className="bg-red-500/10 text-red-600 hover:bg-red-500/20">
                    {attemptedCharacters.toLocaleString()}
                  </Badge>
                </div>
              )}
              {insufficientBy > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('insufficientBy')}</span>
                  <Badge variant="destructive" className="bg-red-500/10 text-red-600 hover:bg-red-500/20">
                    {insufficientBy.toLocaleString()}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Upgrade Benefits */}
          <div className="space-y-4">
            <div className="text-center text-primary">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="h-4 w-4" />
                <span className="font-medium">{t('signupPrompt')}</span>
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="text-lg font-bold">
                {t('charactersBonus')}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t('limitedOffer')}
              </p>
            </div>

            {/* <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <UserPlus className="h-4 w-4 text-green-500" />
                <span>Keep your translation history</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-blue-500" />
                <span>No waiting - translate immediately</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="h-4 w-4 text-purple-500" />
                <span>Access to premium features</span>
              </div>
            </div> */}

          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button onClick={handleCreateAccount} size="lg" className="w-full">
            {t('createAccount')}
          </Button>
          {/* <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
          >
            {t('maybeTomorrow')}
          </Button> */}
        </div>
      </DialogContent>
    </Dialog>
  );
}
